/** Finder-style root lessons page: folder grid + unfiled lessons + search. */
import { useState, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { SearchField, Button, PageContainer } from '@/interfaces/learner/ui';
import { FolderCard } from './components/FolderCard';
import { NewFolderCard } from './components/NewFolderCard';
import { LessonCard } from './components/LessonCard';
import { useFolders, usePersonalLessons } from '@/shared/api/hooks';
import { PAGE_SIZE } from './utils';

// ─── Section label ────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-eyebrow"
      style={{
        color: 'var(--color-text-muted)',
        marginBottom: 'var(--space-3)',
      }}
    >
      {children}
    </p>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LessonsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [unfiledPage, setUnfiledPage] = useState(1);
  const [searchPage, setSearchPage] = useState(1);

  const deferredSearch = useDeferredValue(search);

  const isSearching = deferredSearch.trim().length > 0;

  const { data: folders = [] } = useFolders();

  // Unfiled lessons (no search)
  const { data: unfiledData } = usePersonalLessons(
    !isSearching ? { folderId: 'unfiled', page: unfiledPage, pageSize: PAGE_SIZE } : undefined
  );

  // Search results (flat, all lessons)
  const { data: searchData } = usePersonalLessons(
    isSearching
      ? { search: deferredSearch.trim(), page: searchPage, pageSize: PAGE_SIZE }
      : undefined
  );

  // Reset pages when search changes
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setSearchPage(1);
    setUnfiledPage(1);
  };

  const unfiledLessons = unfiledData?.data ?? [];
  const unfiledPagination = unfiledData?.pagination;

  const searchLessons = searchData?.data ?? [];
  const searchPagination = searchData?.pagination;

  const hasNoContent = !isSearching && folders.length === 0 && unfiledLessons.length === 0;

  return (
    <PageContainer
      style={{
        paddingTop: 'var(--page-top-spacing)',
        paddingBottom: 'var(--space-16)',
      }}
    >
      {/* Back to home */}
      <Button
        variant="ghost"
        size="sm"
        icon={<ArrowLeft size={14} strokeWidth={2} />}
        onPress={() => navigate('/')}
        style={{ marginBottom: 24 }}
      >
        Home
      </Button>

      {/* Page header */}
      <h1
        className="text-section-title"
        style={{
          marginBottom: 'var(--space-5)',
        }}
      >
        My Lessons
      </h1>

      {/* Search */}
      <SearchField
        value={search}
        onChange={handleSearchChange}
        placeholder="Search lessons..."
        size="md"
        style={{ marginBottom: 28 }}
      />

      {/* Empty state: no content at all */}
      {hasNoContent && (
        <div
          style={{
            textAlign: 'center',
            padding: '60px 0',
            color: 'var(--color-text-muted)',
          }}
        >
          <p className="text-body" style={{ color: 'var(--color-text-secondary)' }}>
            No lessons yet
          </p>
          <p className="text-meta-muted" style={{ marginTop: 'var(--space-1)' }}>
            Generate your first lesson from the home page.
          </p>
        </div>
      )}

      {/* Non-search view: folders + unfiled */}
      {!isSearching && !hasNoContent && (
        <>
          {/* Folders section */}
          {folders.length > 0 && (
            <section style={{ marginBottom: 36 }}>
              <SectionLabel>Folders</SectionLabel>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                  gap: 12,
                }}
              >
                {folders.map((folder) => (
                  <FolderCard key={folder.id} folder={folder} />
                ))}
                <NewFolderCard />
              </div>
            </section>
          )}

          {/* NewFolderCard when no folders yet */}
          {folders.length === 0 && (
            <section style={{ marginBottom: 36 }}>
              <SectionLabel>Folders</SectionLabel>
              <div style={{ maxWidth: 180 }}>
                <NewFolderCard />
              </div>
            </section>
          )}

          {/* Unfiled section */}
          <section>
            <SectionLabel>Unfiled</SectionLabel>

            {unfiledLessons.length === 0 ? (
              <p
                style={{
                  color: 'var(--color-text-muted)',
                }}
              >
                No unfiled lessons
              </p>
            ) : (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                    gap: 12,
                  }}
                >
                  {unfiledLessons.map((lesson) => (
                    <LessonCard key={lesson.id} lesson={lesson} />
                  ))}
                </div>

                {unfiledPagination && unfiledPagination.page < unfiledPagination.totalPages && (
                  <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                    <Button variant="ghost" size="sm" onPress={() => setUnfiledPage((p) => p + 1)}>
                      Show more
                    </Button>
                  </div>
                )}
              </>
            )}
          </section>
        </>
      )}

      {/* Search view */}
      {isSearching && (
        <section>
          {searchLessons.length === 0 ? (
            <p
              style={{
                color: 'var(--color-text-muted)',
              }}
            >
              No lessons found
            </p>
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: 12,
                }}
              >
                {searchLessons.map((lesson) => (
                  <LessonCard key={lesson.id} lesson={lesson} />
                ))}
              </div>

              {searchPagination && searchPagination.page < searchPagination.totalPages && (
                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                  <Button variant="ghost" size="sm" onPress={() => setSearchPage((p) => p + 1)}>
                    Show more
                  </Button>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </PageContainer>
  );
}
